<script setup lang="ts">
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { computed, onMounted, ref, watch } from 'vue'
  import { useUserStore } from '@/shared/stores/userStore'
  import {
    type CustomerUser,
    type LawfirmSettings,
    type LawfirmSettingsRequest,
    RoleEnum,
    SettingsService
  } from '@/types/generated'
  import * as Sentry from '@sentry/vue'
  import { toast } from '@/components/ui/toast'

  const isLoading = ref(false)
  const { fetchUser } = useUserStore()
  const user = computed((): CustomerUser => useUserStore().user as CustomerUser)

  // LawFirm integration fields
  const isLawfirmEnabled = ref(false)

  const { t } = useTranslation({
    en: {
      warning: 'Warning',
      onlyAdminsAllowedMsg: 'Only the administrator is allowed to edit this setting.',
      description: 'Configure LawFirm integration settings for JUPUS.',
      enableLawfirm: 'Enable LawFirm Integration',
      saving: 'Saving...',
      success: 'Success',
      saveSuccessMsg: 'LawFirm settings saved successfully.',
      error: 'Error',
      saveErrorMsg: 'Failed to save LawFirm settings.'
    },
    de: {
      warning: 'Achtung',
      onlyAdminsAllowedMsg: 'Nur der Administrator kann diese Einstellung bearbeiten.',
      description: 'Die LawFirm Schnittstelle',
      enableLawfirm: 'LawFirm Integration aktivieren',
      saving: 'Speichern...',
      success: 'Erfolg',
      saveSuccessMsg: 'LawFirm Einstellungen erfolgreich gespeichert.',
      error: 'Fehler',
      saveErrorMsg: 'Speichern der LawFirm Einstellungen fehlgeschlagen.'
    }
  })

  const internalLawfirmUpdate = async (data: LawfirmSettingsRequest) => {
    if (!user.value) {
      throw Error('User is not defined. Cannot update LawFirm settings.')
    }

    await SettingsService.settingsLawfirmCreate({
      requestBody: data
    })
  }

  const updateLawfirmSettings = async () => {
    try {
      isLoading.value = true

      // Create data object with only the fields that have values
      const data: LawfirmSettingsRequest = {
        is_law_firm_software_exportation_enabled: isLawfirmEnabled.value
      }

      await internalLawfirmUpdate(data)
      toast({
        title: t('success'),
        description: t('saveSuccessMsg'),
        variant: 'default'
      })
    } catch (error) {
      Sentry.captureException(error)
      toast({
        title: t('error'),
        description: t('saveErrorMsg'),
        variant: 'destructive'
      })
    } finally {
      isLoading.value = false
    }
  }

  onMounted(async () => {
    if (!user.value) {
      await fetchUser()
    }

    try {
      const lawfirmSettings: LawfirmSettings =
        (await SettingsService.settingsLawfirmList()) as LawfirmSettings

      // Initialize form with existing values if available
      if (lawfirmSettings) {
        isLawfirmEnabled.value = lawfirmSettings.is_law_firm_software_exportation_enabled || false
      }
    } catch (error) {
      console.error('Error loading LawFirm settings:', error)
      Sentry.captureException(error)
    }
  })

  // Watch for changes to update the UI accordingly
  watch([isLawfirmEnabled], () => {
    // Only update if we have a user and the component is mounted
    if (user.value) {
      updateLawfirmSettings()
    }
  })
</script>

<template>
  <SettingsPage v-if="user">
    <div class="space-y-8">
      <div
        v-if="user.role !== RoleEnum.ADMIN"
        class="mb-6"
      >
        <div class="mb-4 w-full rounded-md bg-orange-100 p-4 text-orange-700">
          <p class="font-medium">{{ t('warning') }}</p>
          <p class="mt-2">{{ t('onlyAdminsAllowedMsg') }}</p>
        </div>
      </div>
      <div
        v-else
        class="mb-6"
      >
        <!-- LawFirm Integration Settings -->
        <div class="max-w-xl space-y-4">
          <!-- Enable LawFirm Integration -->
          <CheckboxField
            v-model="isLawfirmEnabled"
            name="is_law_firm_software_exportation_enabled"
            :label="t('enableLawfirm')"
          />
        </div>
      </div>
    </div>
  </SettingsPage>
</template>

<style scoped>
  /* Add any scoped styles here if needed */
</style>
